import { t } from 'i18next';
import { DateTime } from 'luxon';

import { FilterType } from '@work4all/components';
import { getTimeSpanUnit } from '@work4all/components/lib/dataDisplay/basic-table/components/date-range-picker/DateRangePicker';

import { ServiceContract } from '@work4all/models/lib/Classes/ServiceContract.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { RelativeDateFilter } from '@work4all/utils/lib/date-utils/RelativeDateFilter.enum';

import { useServiceContractActions } from '../../containers/mask-overlays/mask-overlay/views/service-contract-billing/hooks/use-service-contract-actions';
import { useBottomViews } from '../bottom-views';
import { OrganisationArea } from '../organisation-area';

import { EntityTableV8, IEntityTableV8 } from './entity-table/EntityTableV8';
import { useEntityDataTableV8 } from './entity-table/use-entity-data-table-v8';
import schema from './schemata/serviceContract-table-schema.json';
import { ServiceContractRythmCell } from './ServiceContractRythmCell';
import { useErpHandlersTemplateV8 } from './use-erp-handlers-template-v8';

const defaultSort = [
  {
    field: 'mainAddress.businessPartner.data.name',
    direction: SortDirection.ASCENDING,
  },
];

export function ServiceContractTable(props: IEntityTableV8) {
  const dataTable = useEntityDataTableV8<ServiceContract, never>({
    ...props,
    schema: schema as never,
    defaultSort,
    defaultColumnFilters: [
      {
        id: 'nextInvoiceDate',
        value: {
          filterType: FilterType.Date,
          value: {
            startDate: RelativeDateFilter.todayAndPast,
            span: getTimeSpanUnit(RelativeDateFilter.todayAndPast, null),
          },
        },
      },
    ],
    cells: {
      RythmStatus: ServiceContractRythmCell,
    },
    rowModifiers: (value) => {
      if (!value.nextInvoiceDate) {
        return {
          isSuccess: false,
          isError: false,
          isShade1: true,
        };
      }

      const now = DateTime.now();
      const nextInvoiceDate = DateTime.fromISO(value.nextInvoiceDate);

      return {
        isSuccess: nextInvoiceDate.isValid && now < nextInvoiceDate,
        isError: nextInvoiceDate.isValid && now > nextInvoiceDate,
      };
    },
  });

  const template = useErpHandlersTemplateV8(
    dataTable.table,
    'mainAddress.businessPartner.data.id',
    Entities.customer
  );

  const { entity: entityType } = schema as never;

  const prefilter = [{ id: { $eq: dataTable.selectedEntity?.id ?? null } }];
  const bottomArea = useBottomViews({
    views: [
      {
        type: 'table',
        entity: Entities.serviceContractPosition,
        titleTranslationKey: 'COMMON.POSITIONS',
        options: {
          prefilter,
        },
      },
    ],
  });

  const { left } = useServiceContractActions({
    refetch: dataTable.refetch,
    resetSelection: dataTable.table.resetRowSelection,
    data: dataTable.data,
    selectedEntities: dataTable.selectedEntities,
  });

  return (
    <EntityTableV8
      {...props}
      template={template}
      areas={{
        left: {
          title: t('COMMON.ORGANISATION'),
          content: (
            <OrganisationArea entity={entityType} table={dataTable.table} />
          ),
          resizable: true,
          collapsible: true,
          custom: true,
        },
        bottom: bottomArea,
        right: undefined,
      }}
      {...dataTable}
      actions={{
        custom: {
          left,
        },
      }}
    />
  );
}
